import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AuditEvent,
  type Translate,
  useWorkspaceAudit,
  useWorkspaceMembers,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import type { Workspace } from '@/entities/workspace';
import { describedFrom, useJournalSearch } from '@/shared/lib/journal-search';
import { Avatar, Button, ErrorState, Icons, LoadingState, SearchField } from '@/shared/ui';

import styles from './Admin.module.css';

type Glyph = () => React.JSX.Element;

/** The icon of an event by its family (the part before the first dot). */
function glyphOf(eventType: string): Glyph {
  const family = eventType.split('.')[0] ?? '';
  switch (family) {
    case 'workspace':
      return Icons.workspace;
    case 'member':
      return Icons.users;
    case 'team':
      return Icons.layers;
    case 'role':
      return Icons.shield;
    case 'board_label':
    case 'board':
      return eventType.startsWith('board.owner') ? Icons.crown : Icons.board;
    case 'access_rule':
      return Icons.key;
    case 'invitation':
    case 'invite_link':
      return Icons.mail;
    case 'request':
    case 'transfer':
      return Icons.inbox;
    default:
      return Icons.history;
  }
}

/** `group.header_image_changed` -> `group header image changed`: no dots or underscores shown. */
function humanize(eventType: string): string {
  return eventType.replace(/[._]+/g, ' ').trim();
}

/** The name of the object an event is about, as it was named at that moment. */
function objectName(payload: Record<string, unknown>): string | null {
  for (const key of ['name', 'board_name', 'filename', 'title']) {
    const value = payload[key];
    if (typeof value === 'string' && value) return value;
  }
  return null;
}

function dayKey(iso: string, zone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      dateStyle: 'short',
      ...(zone && { timeZone: zone }),
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Read-only trail of administrative events: grouped by day, newest first, with an icon per kind,
 * the actor prominent, and the affected object named. Everything shown comes from the audit
 * record itself (nothing is invented); deleted people keep the name they had at the time.
 */
export function AuditTab({ workspace }: { workspace: Workspace }) {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as Translate;
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  // the localized descriptions let a person search by "Group created" without knowing identifiers
  const catalog = useMemo(() => describedFrom(t('audit.events', { returnObjects: true })), [t]);
  const search = useJournalSearch(catalog);
  const events = useWorkspaceAudit(workspace.id, true, { q: search.q, types: search.types });
  const members = useWorkspaceMembers(workspace.id);
  const names = useMemo(
    () => new Map((members.data ?? []).map((m) => [m.user_id, m.display_name])),
    [members.data],
  );
  const all: AuditEvent[] = useMemo(() => events.data?.pages.flat() ?? [], [events.data]);
  const groups = useMemo(() => {
    const result: { day: string; label: string; items: AuditEvent[] }[] = [];
    for (const event of all) {
      const key = dayKey(event.created_at, zone);
      let group = result.find((g) => g.day === key);
      if (!group) {
        let label = key;
        try {
          label = new Intl.DateTimeFormat(i18n.language, {
            dateStyle: 'full',
            ...(zone && { timeZone: zone }),
          }).format(new Date(event.created_at));
        } catch {
          /* keep the numeric day */
        }
        group = { day: key, label, items: [] };
        result.push(group);
      }
      group.items.push(event);
    }
    return result;
  }, [all, i18n.language, zone]);

  const time = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        timeStyle: 'short',
        ...(zone && { timeZone: zone }),
      }).format(new Date(iso));
    } catch {
      return iso.slice(11, 16);
    }
  };
  let body: React.JSX.Element;
  if (events.isPending) body = <LoadingState />;
  else if (events.isError) body = <ErrorState error={events.error} />;
  else if (all.length === 0) {
    body = (
      <p className={styles.note} data-testid="audit-empty">
        {search.searching
          ? t('journal.noMatches', { q: search.input.trim() })
          : t('workspaceAdmin.auditEmpty')}
      </p>
    );
  } else {
    body = (
      <>
        {groups.map((group) => (
          <section key={group.day} aria-label={group.label} className={styles.auditDay}>
            <h3 className={styles.auditDayHeading}>{group.label}</h3>
            <ul className={styles.list} aria-label={t('workspaceAdmin.tabs.audit')}>
              {group.items.map((event) => {
                const key = `audit.events.${event.event_type.replace(/\./g, '_')}`;
                const text = tr(key);
                const Glyph = glyphOf(event.event_type);
                const object = objectName(event.payload);
                const subject = event.subject_user_id
                  ? (names.get(event.subject_user_id) ?? null)
                  : null;
                const actor = event.actor_name ?? t('workspaceAdmin.auditUnknownActor');
                return (
                  <li key={event.id} className={styles.auditRow} data-testid="audit-event">
                    <span className={styles.auditIcon}>
                      <Glyph />
                    </span>
                    <span className={styles.grow}>
                      <span className={styles.auditLine}>
                        {event.actor_user_id && (
                          <Avatar id={event.actor_user_id} name={actor} size="sm" />
                        )}
                        <strong className={styles.auditActor}>{actor}</strong>
                        <span>
                          {text === key
                            ? t('journal.unknownEvent', { type: humanize(event.event_type) })
                            : text}
                        </span>
                      </span>
                      {(object || subject) && (
                        <span className={styles.auditObjects}>
                          {object && <span className={styles.chip}>{object}</span>}
                          {subject && <span className={styles.chip}>{subject}</span>}
                        </span>
                      )}
                    </span>
                    <time className={styles.meta} dateTime={event.created_at}>
                      {time(event.created_at)}
                    </time>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {events.hasNextPage && (
          <div className={styles.actions}>
            <Button
              disabled={events.isFetchingNextPage}
              onClick={() => {
                void events.fetchNextPage();
              }}
            >
              {t('workspaceAdmin.auditMore')}
            </Button>
          </div>
        )}
      </>
    );
  }
  return (
    <div className={styles.panel}>
      <SearchField
        value={search.input}
        onChange={search.setInput}
        label={t('journal.search')}
        placeholder={t('journal.placeholder')}
        clearLabel={t('journal.clear')}
        testId="audit-search"
      />
      {body}
    </div>
  );
}
